import React, { useCallback, useState } from 'react'
import { BaseStepInputProps, UseStepFunc, defaultStepFormProps } from '../types.d'
import { VatNumberForm } from './VatNumberForm'
import { Route, Switch } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { OnboardingStepKey } from '@cbgms/api'
import AlreadyRegisteredVAT from './AlreadyRegisteredVAT'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'
import GenericDuplicationError from 'app/auth/components/onboarding/steps/shared/GenericDuplicationError'
import InviteUserToOtherShop from 'app/auth/components/onboarding/steps/shared/InviteUserToOtherShop'

interface IStepProps {
  onSubmit: (vatNumber: string) => void
  basePath: string
  country?: string
}
type OnboardingCheckVATStepProps = IStepProps

interface IStepInputProps extends BaseStepInputProps {
  country?: string
}
interface StepOutput {
  validatedVATNumber: string | null
}

export const useOnboardingCheckVATStep: UseStepFunc<IStepProps, IStepInputProps, StepOutput> = ({ basePath, country, onboardingInfo }) => {
  const [validatedVATNumber, setValidatedVATNumber] = useState<string | null>(null)
  const stepName = OnboardingStepKey.VatDuplicateCheck
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const onStepSubmit = useCallback(
    (result: string) => {
      setValidatedVATNumber(result)
    },
    [setValidatedVATNumber]
  )
  const complete = useCompleteOnSubmit(onStepSubmit)

  return {
    Component: OnboardingCheckVATStep,
    CTAComponent: null,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip: true,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      onSubmit: complete.onSubmit,
      basePath,
      country
    },
    persister: {
      onPersist: async () => {}, // do nothing
      persistOrder: 0
    },
    validatedVATNumber // we expose the filled in vat for next steps.
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_check_vat')}</>
}

export const OnboardingCheckVATStep: React.FC<OnboardingCheckVATStepProps & defaultStepFormProps> = ({ basePath, onSubmit, ...rest }) => {
  return (
    <Switch>
      <Route path={routing.pathAlreadyRegisteredVAT(basePath)} component={AlreadyRegisteredVAT} />
      <Route path={routing.pathDuplicationError(routing.pathCheckVatNumber(basePath))} component={GenericDuplicationError} />
      <Route path={routing.pathInviteUserToOtherShop(routing.pathCheckVatNumber(basePath))} component={InviteUserToOtherShop} />
      <Route
        path={routing.pathCheckVatNumber(basePath)}
        render={() => <VatNumberForm basePath={basePath} onSubmit={onSubmit} {...rest} />}
      />
    </Switch>
  )
}
